import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import { PageHeader, StatusBadge } from "../lib/ui";
import { isoWeek, weekOptions } from "../lib/week";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Siren, CheckCircle2, XCircle, Info } from "lucide-react";

const LEVEL_STYLE = {
  Urgent: "border-l-red-500 bg-red-50/50", Important: "border-l-amber-500 bg-amber-50/50",
  "À surveiller": "border-l-blue-500 bg-blue-50/50", Informatif: "border-l-slate-400 bg-slate-50/50",
};

export default function Signals() {
  const cur = isoWeek();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [week, setWeek] = useState(cur.week);
  const [year, setYear] = useState(cur.year);
  const canAct = ["si_analyst", "provincial_supervisor", "admin"].includes(user?.role);

  const { data, isLoading } = useQuery({
    queryKey: ["signals", week, year, lang],
    queryFn: async () => (await api.get("/signals", { params: { week, year, lang } })).data,
  });

  const setStatus = async (sig, status) => {
    await api.post("/signals/status", { id: sig.id, week, year, status });
    toast.success(`${t("Signal marqué")}: ${t(status)}`);
    qc.invalidateQueries({ queryKey: ["signals"] });
  };

  return (
    <div data-testid="signals-page">
      <PageHeader title={t("Signaux & Alertes de Surveillance")} subtitle={t("Détection automatique d'aide à la décision")} />

      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6 flex gap-3 text-sm text-amber-800">
        <Info className="h-5 w-5 shrink-0" />
        <p>{data?.disclaimer}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 shadow-sm flex gap-3 items-end">
        <div><Label className="text-xs">{t("Semaine")}</Label>
          <Select value={String(week)} onValueChange={(v) => setWeek(Number(v))}>
            <SelectTrigger className="w-28" data-testid="signals-week"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">{weekOptions().map((w) => <SelectItem key={w} value={String(w)}>S{String(w).padStart(2, "0")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">{t("Année")}</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{[cur.year, cur.year - 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? <p className="text-slate-400 text-sm">{t("Analyse…")}</p> : (
        <div className="space-y-3">
          {data?.signals?.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-400">{t("Aucun signal détecté pour cette semaine.")}</div>
          ) : data.signals.map((s) => (
            <div key={s.id} className={`bg-white border border-slate-200 border-l-4 rounded-md p-4 shadow-sm ${LEVEL_STYLE[s.level]}`} data-testid={`signal-${s.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3">
                  <Siren className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{s.type}</span>
                      <StatusBadge status={s.level} />
                      <span className="text-xs text-slate-500">{t("Zone")}: {s.zone}</span>
                      {s.status !== "Nouveau" && <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{t(s.status)}</span>}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{s.detail}</p>
                  </div>
                </div>
                {canAct && (
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => setStatus(s, "Confirmé")} data-testid={`confirm-${s.id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> {t("Confirmer")}</Button>
                    <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => setStatus(s, "Rejeté")} data-testid={`dismiss-${s.id}`}><XCircle className="h-4 w-4 mr-1" /> {t("Rejeter")}</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
